import {AxiosProgressEvent} from 'axios';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {readableSize} from 'presentation/utils';
import {cleanFileName} from 'presentation/utils/cleanFileName';
import {useCallback, useState} from 'react';
import {useUploadedRefundFilesHistoryStore} from '../../store/useUploadedRefundFilesHistoryStore';
import {useUploadedRefundFilesStore} from '../../store/useUploadedRefundFilesStore';
import {IFile} from '../../types';
import {UseUploadFileProps} from './types';

export function useUploadRefundFile({
	deleteFactory,
	postFactory,
	protocolCode,
	requestCode,
	clearErrorMessage,
}: UseUploadFileProps) {
	const uploadedFiles = useUploadedRefundFilesStore(s => s.uploadedFiles);
	const setUploadedFiles = useUploadedRefundFilesStore(s => s.setUploadedFiles);
	const updateFile = useUploadedRefundFilesStore(s => s.updateFile);
	const removeFilesWithError = useUploadedRefundFilesStore(s => s.removeFilesWithError);
	const removeFile = useUploadedRefundFilesStore(s => s.removeFile);

	const updateFileHistory = useUploadedRefundFilesHistoryStore(s => s.updateFile);
	const setUploadedFilesHistory = useUploadedRefundFilesHistoryStore(s => s.setUploadedFiles);
	const removeFilesWithErrorHistory = useUploadedRefundFilesHistoryStore(
		s => s.removeFilesWithError,
	);
	const removeFileHistory = useUploadedRefundFilesHistoryStore(s => s.removeFile);

	const [deleteState, setDeleteState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);
	const IS_DELETING = deleteState === OperationStateTypesEnum.SUBMITTING;
	const IS_DELETING_ERROR = deleteState === OperationStateTypesEnum.ERROR;
	const IS_UPLOADING = uploadedFiles.some(file => file.uploaded !== true && file.error !== true);

	const randomId = crypto.randomUUID();

	const blockCloseModal = useCallback(() => {
		setDeleteState(OperationStateTypesEnum.SUBMITTING);
	}, []);

	const processUpload = useCallback(
		async (uploadedFile: IFile) => {
			if (uploadedFile.file) {
				const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
					if (progressEvent.total !== undefined) {
						const progress: number = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total,
						);
						updateFile(uploadedFile.id, {progress});
						updateFileHistory(uploadedFile.id, {progress});
					}
				};

				try {
					const response = await postFactory(onUploadProgress).post({
						file: uploadedFile.file,
						'protocol-code': protocolCode,
						'request-code': requestCode,
					});

					updateFile(uploadedFile.id, {
						uploaded: true,
						id: response.content.attachmentCode.toString(),
					});
					updateFileHistory(uploadedFile.id, {
						uploaded: true,
						id: response.content.attachmentCode.toString(),
					});
				} catch (error) {
					updateFile(uploadedFile.id, {
						error: true,
					});
					updateFileHistory(uploadedFile.id, {
						error: true,
					});
				}
			}
		},
		[updateFile, updateFileHistory],
	);

	const handleUpload = useCallback(
		(files: File[]) => {
			if (clearErrorMessage) clearErrorMessage();
			removeFilesWithError();
			removeFilesWithErrorHistory();
			const newUploadedFiles: IFile[] = files.map((file: File) => ({
				file: new File([file], cleanFileName(file.name), {
					type: file.type,
				}),
				id: randomId,
				filename: cleanFileName(file.name),
				readableSize: readableSize(file.size),
				progress: 0,
				uploaded: false,
				error: false,
				protocolCode,
				requestCode,
			}));

			setUploadedFiles(state => {
				const nonDuplicateFiles = state.filter(
					file =>
						!newUploadedFiles.some(
							newFile =>
								newFile.filename === file.filename &&
								newFile.requestCode === file.requestCode,
						),
				);
				return nonDuplicateFiles.concat(newUploadedFiles);
			});
			setUploadedFilesHistory(state => {
				const nonDuplicateFiles = state.filter(
					file =>
						!newUploadedFiles.some(
							newFile =>
								newFile.filename === file.filename &&
								newFile.requestCode === file.requestCode,
						),
				);
				return nonDuplicateFiles.concat(newUploadedFiles);
			});
			newUploadedFiles.forEach(processUpload);
		},
		[processUpload, randomId, clearErrorMessage],
	);

	const resetDeleteError = () => setDeleteState(OperationStateTypesEnum.NONE);

	const deleteFile = useCallback(async (attachmentId: string) => {
		try {
			setDeleteState(OperationStateTypesEnum.SUBMITTING);

			await deleteFactory(attachmentId).delete();
			setDeleteState(OperationStateTypesEnum.DONE);
			removeFile(attachmentId);
			removeFileHistory(attachmentId);
		} catch (error) {
			setDeleteState(OperationStateTypesEnum.ERROR);
		}
	}, []);

	return {
		uploadedFiles,
		handleUpload,
		deleteFile,
		blockCloseModal,
		IS_DELETING,
		IS_UPLOADING,
		IS_DELETING_ERROR,
		resetDeleteError,
	};
}
